import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
} from '@mui/material';
import { fetchLogistics } from '../../services/logistics';
import { assistedPayPackage, assistedSelectShipping, searchAssistedPackages } from '../../services/package';
import { getCurrencyRates } from '../../services/currency';

const DEFAULT_SHIPPING_FORM = {
  shipping_option: 'platform',
  shipping_method: 'sea',
  logistics_id: '',
  agent_name: '',
  agent_address: '',
  agent_city: '',
  agent_province: '',
  contact_person: '',
  contact_phone: '',
};

const currency_converter = (defaultCurrency, preferredCurrency, price, rate) => {
  if (defaultCurrency === preferredCurrency) {
    return price;
  }
  if (defaultCurrency === 'NGN') {
    return price / rate;
  }
  return price * rate;
};

const AssistedPackageManagement = () => {
  const [userQuery, setUserQuery] = useState('');
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [shippingForm, setShippingForm] = useState(DEFAULT_SHIPPING_FORM);
  const [logisticsOptions, setLogisticsOptions] = useState([]);
  const [payInternational, setPayInternational] = useState(true);
  const [payLocal, setPayLocal] = useState(false);
  const [submittingShipping, setSubmittingShipping] = useState(false);
  const [submittingPayment, setSubmittingPayment] = useState(false);
  const [currencyRate, setCurrencyRate] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, severity: 'success', message: '' });

  const showSnackbar = useCallback((severity, message) => {
    setSnackbar({ open: true, severity, message });
  }, []);

  const loadPackages = useCallback(async () => {
    const trimmedUserQuery = userQuery.trim();
    if (!trimmedUserQuery) {
      setPackages([]);
      setSelectedPackage(null);
      setHasSearched(true);
      showSnackbar('warning', 'Enter username, email, phone, or BIN code before searching');
      return;
    }
    setLoading(true);
    try {
      const response = await searchAssistedPackages({
        page: 1,
        page_size: 50,
        user: trimmedUserQuery,
      });
      setHasSearched(true);
      setPackages(response?.results || []);
      if (selectedPackage) {
        const refreshed = (response?.results || []).find((pkg) => pkg.id === selectedPackage.id);
        if (refreshed) {
          setSelectedPackage(refreshed);
        } else {
          setSelectedPackage(null);
        }
      }
    } catch (error) {
      showSnackbar('error', error?.response?.data?.error || error.message || 'Failed to load packages');
    } finally {
      setLoading(false);
    }
  }, [userQuery, selectedPackage, showSnackbar]);

  useEffect(() => {
    const loadLogistics = async () => {
      try {
        const response = await fetchLogistics({
          page: 1,
          page_size: 100,
          method: shippingForm.shipping_method,
        });
        setLogisticsOptions(response?.results || []);
      } catch (error) {
        showSnackbar('error', error?.message || 'Failed to load logistics providers');
      }
    };
    loadLogistics();
  }, [shippingForm.shipping_method, showSnackbar]);

  useEffect(() => {
    const fetchCurrencyRate = async () => {
      try {
        const response = await getCurrencyRates({ page: 1, page_size: 100 });
        const rates = response?.results || [];
        const ngnToCnyRate = rates.find((rate) => (
          rate.base_currency_code === 'NGN' && rate.target_currency_code === 'CNY'
        ));
        if (ngnToCnyRate?.base_currency_rate) {
          setCurrencyRate(parseFloat(ngnToCnyRate.base_currency_rate));
        }
      } catch (error) {
        setCurrencyRate(null);
      }
    };
    fetchCurrencyRate();
  }, []);

  const packagePaymentPreview = useMemo(() => {
    if (!selectedPackage) return { international: 0, local: 0, total: 0 };
    const internationalRmb = Number(selectedPackage.international_shipping_cost || 0);
    const localRmb = Number(selectedPackage.local_logistics_fee || 0);
    const international = currencyRate
      ? currency_converter('CNY', 'NGN', internationalRmb, currencyRate)
      : internationalRmb;
    const local = currencyRate
      ? currency_converter('CNY', 'NGN', localRmb, currencyRate)
      : localRmb;
    const total = (payInternational ? international : 0) + (payLocal ? local : 0);
    return { international, local, total };
  }, [selectedPackage, payInternational, payLocal, currencyRate]);

  const formatNaira = useCallback((amount) => (
    Number(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  ), []);

  const handleSelectPackage = (pkg) => {
    setSelectedPackage(pkg);
    setShippingForm((prev) => ({
      ...prev,
      shipping_method: pkg.shipping_method || prev.shipping_method,
    }));
    setPayLocal(Boolean(pkg.local_logistics_fee) && !pkg.local_logistics_fee_paid);
    setPayInternational(true);
  };

  const handleShippingInputChange = (field) => (event) => {
    setShippingForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSubmitShipping = async () => {
    if (!selectedPackage) {
      showSnackbar('warning', 'Select a package first');
      return;
    }
    setSubmittingShipping(true);
    try {
      const payload = {
        shipping_option: shippingForm.shipping_option,
        shipping_method: shippingForm.shipping_method,
      };
      if (shippingForm.shipping_option === 'platform') {
        payload.logistics_id = shippingForm.logistics_id;
      } else {
        payload.agent_name = shippingForm.agent_name;
        payload.agent_address = shippingForm.agent_address;
        payload.agent_city = shippingForm.agent_city;
        payload.agent_province = shippingForm.agent_province;
        payload.contact_person = shippingForm.contact_person;
        payload.contact_phone = shippingForm.contact_phone;
      }
      const response = await assistedSelectShipping(selectedPackage.id, payload);
      showSnackbar('success', response?.message || 'Shipping selection updated');
      setSelectedPackage(response?.package || selectedPackage);
      loadPackages();
    } catch (error) {
      showSnackbar('error', error?.response?.data?.error || error.message || 'Failed to update shipping');
    } finally {
      setSubmittingShipping(false);
    }
  };

  const handleSubmitPayment = async () => {
    if (!selectedPackage) {
      showSnackbar('warning', 'Select a package first');
      return;
    }
    setSubmittingPayment(true);
    try {
      const response = await assistedPayPackage(selectedPackage.id, {
        pay_international: payInternational,
        pay_local: payLocal,
      });
      showSnackbar('success', response?.message || 'Payment completed');
      setSelectedPackage(response?.package || selectedPackage);
      loadPackages();
    } catch (error) {
      showSnackbar('error', error?.response?.data?.error || error.message || 'Failed to process payment');
    } finally {
      setSubmittingPayment(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
        Assisted Package Management
      </Typography>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 10 }}>
              <TextField
                label="Search user"
                value={userQuery}
                onChange={(event) => {
                  setUserQuery(event.target.value);
                  setPackages([]);
                  setSelectedPackage(null);
                  setHasSearched(false);
                }}
                fullWidth
                placeholder="Enter exact username, email, phone, or BIN code"
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    loadPackages();
                  }
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <Button
                variant="contained"
                fullWidth
                onClick={loadPackages}
                sx={{ height: '56px' }}
              >
                Find
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 7 }}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                Packages
              </Typography>
              {loading ? (
                <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress size={28} />
                </Box>
              ) : (
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Package</TableCell>
                        <TableCell>User</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Method</TableCell>
                        <TableCell align="right">Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {packages.map((pkg) => (
                        <TableRow key={pkg.id} selected={selectedPackage?.id === pkg.id}>
                          <TableCell>{pkg.package_number}</TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {pkg.user_info?.username || pkg.user_info?.email || 'N/A'}
                            </Typography>
                            {pkg.user_info?.bin_code && (
                              <Typography variant="caption" color="text.secondary">
                                BIN: {pkg.user_info.bin_code}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>{pkg.packaging_status}</TableCell>
                          <TableCell>{pkg.shipping_method || '-'}</TableCell>
                          <TableCell align="right">
                            <Button size="small" variant="outlined" onClick={() => handleSelectPackage(pkg)}>
                              Manage
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {!hasSearched && (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            Search by username, email, phone, or BIN code to load ready-to-ship packages.
                          </TableCell>
                        </TableRow>
                      )}
                      {hasSearched && packages.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            No packages found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 5 }}>
          <Stack spacing={2}>
            <Card>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                  Shipping Selection
                </Typography>
                {selectedPackage ? (
                  <Stack spacing={1.5}>
                    <Typography variant="body2">
                      {selectedPackage.package_number} · {selectedPackage.user_info?.bin_code || selectedPackage.user_info?.email || 'N/A'}
                    </Typography>
                    <FormControl fullWidth>
                      <InputLabel>Shipping Option</InputLabel>
                      <Select
                        value={shippingForm.shipping_option}
                        label="Shipping Option"
                        onChange={handleShippingInputChange('shipping_option')}
                      >
                        <MenuItem value="platform">Platform</MenuItem>
                        <MenuItem value="self">Self</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl fullWidth>
                      <InputLabel>Shipping Method</InputLabel>
                      <Select
                        value={shippingForm.shipping_method}
                        label="Shipping Method"
                        onChange={handleShippingInputChange('shipping_method')}
                      >
                        <MenuItem value="sea">Sea</MenuItem>
                        <MenuItem value="air">Air</MenuItem>
                      </Select>
                    </FormControl>

                    {shippingForm.shipping_option === 'platform' ? (
                      <FormControl fullWidth>
                        <InputLabel>Logistics Provider</InputLabel>
                        <Select
                          value={shippingForm.logistics_id}
                          label="Logistics Provider"
                          onChange={handleShippingInputChange('logistics_id')}
                        >
                          {logisticsOptions.map((item) => (
                            <MenuItem key={item.id} value={item.id}>
                              {item.shipping_agent} ({item.method}) - ₦{Number(item.total_fee || 0).toLocaleString()}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <>
                        <TextField label="Agent Name" value={shippingForm.agent_name} onChange={handleShippingInputChange('agent_name')} fullWidth />
                        <TextField label="Agent Address" value={shippingForm.agent_address} onChange={handleShippingInputChange('agent_address')} fullWidth />
                        <TextField label="Agent City" value={shippingForm.agent_city} onChange={handleShippingInputChange('agent_city')} fullWidth />
                        <TextField label="Agent Province" value={shippingForm.agent_province} onChange={handleShippingInputChange('agent_province')} fullWidth />
                        <TextField label="Contact Person" value={shippingForm.contact_person} onChange={handleShippingInputChange('contact_person')} fullWidth />
                        <TextField label="Contact Phone" value={shippingForm.contact_phone} onChange={handleShippingInputChange('contact_phone')} fullWidth />
                      </>
                    )}

                    <Button
                      variant="contained"
                      onClick={handleSubmitShipping}
                      disabled={submittingShipping}
                      startIcon={submittingShipping ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                      Update Shipping
                    </Button>
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Select a package to manage shipping.
                  </Typography>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                  Wallet Payment
                </Typography>
                {selectedPackage ? (
                  <Stack spacing={1.5}>
                    <FormControlLabel
                      control={<Checkbox checked={payInternational} onChange={(event) => setPayInternational(event.target.checked)} />}
                      label={`International fee (₦${formatNaira(packagePaymentPreview.international)})`}
                    />
                    <FormControlLabel
                      control={<Checkbox checked={payLocal} onChange={(event) => setPayLocal(event.target.checked)} />}
                      label={`Local logistics fee (₦${formatNaira(packagePaymentPreview.local)})`}
                    />
                    <Typography variant="body2" fontWeight={600}>
                      Total debit: ₦{formatNaira(packagePaymentPreview.total)}
                    </Typography>
                    <Button
                      variant="contained"
                      color="success"
                      onClick={handleSubmitPayment}
                      disabled={submittingPayment}
                      startIcon={submittingPayment ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                      Pay from Wallet
                    </Button>
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Select a package to process wallet payment.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AssistedPackageManagement;
